import { type ImageSource } from "../../types";
import { type Scene } from "../../scene";
import { Constants } from "../../Engines/constants";
import { _IsSideEffectImplemented, _WarnImport } from "../../Misc/devTools";
import { RawTexture2DArray } from "./rawTexture2DArray";

/**
 * These helpers populate 2D array texture layers from decoded image sources.
 * They rely on the AbstractEngine.updateTextureArrayLayerFromImageSource engine extension. The full
 * Engine build registers it for both backends at load time. On the side-effect-free (pure /
 * tree-shaken) path it is opt-in: import the matching module for your backend before use:
 * - WebGL2:  import "core/Engines/Extensions/engine.texture2DArrayImageSource";
 * - WebGPU:  import "core/Engines/WebGPU/Extensions/engine.texture2DArrayImageSource";
 *
 * Consuming the result: the built-in way to sample a chosen layer is Node Material's Texture block,
 * which exposes a `layer` input (feed it a Float) and samples the array at that layer for you — no
 * custom shader code required. If you instead write your own shader, declare a `sampler2DArray`
 * (GLSL) / `texture_2d_array<f32>` (WGSL) uniform and sample it with an explicit integer layer index;
 * the classic StandardMaterial / PBRMaterial texture slots are plain 2D and cannot read an array layer.
 */

/**
 * Options controlling how an image source is uploaded into a 2D array texture layer.
 */
export interface IUploadImageToTexture2DArrayLayerOptions {
    /** Defines if the source must be stored with the Y axis inverted (false by default) */
    invertY?: boolean;
    /** Defines if the source alpha must be premultiplied (false by default) */
    premultiplyAlpha?: boolean;
}

/**
 * Options controlling the creation of a 2D array texture from a list of image urls.
 */
export interface ICreateTexture2DArrayFromImageUrlsOptions extends IUploadImageToTexture2DArrayLayerOptions {
    /** Defines if mip levels should be generated (true by default) */
    generateMipMaps?: boolean;
    /** Defines the sampling mode to use (Texture.TRILINEAR_SAMPLINGMODE by default) */
    samplingMode?: number;
    /** Defines the texture type (Constants.TEXTURETYPE_UNSIGNED_BYTE by default) */
    textureType?: number;
    /** Options forwarded to createImageBitmap when decoding each url */
    imageBitmapOptions?: ImageBitmapOptions;
}

/**
 * Uploads a decoded image source (ImageBitmap, canvas, video, image element...) into a single layer of a 2D array texture.
 * This is the image-source counterpart to RawTexture2DArray.update, which only accepts raw bytes.
 * @param texture defines the 2D array texture to upload into
 * @param source defines the image source to upload
 * @param layer defines the array layer to upload into
 * @param options defines optional upload settings (invertY, premultiplyAlpha)
 */
export function UploadImageToTexture2DArrayLayer(texture: RawTexture2DArray, source: ImageSource, layer: number, options?: IUploadImageToTexture2DArrayLayerOptions): void {
    const internalTexture = texture.getInternalTexture();
    if (!internalTexture) {
        throw new Error("Cannot upload to a 2D array texture that has no internal texture.");
    }

    if (!Number.isInteger(layer) || layer < 0 || layer >= texture.depth) {
        throw new Error(`Layer ${layer} is out of range for a 2D array texture with ${texture.depth} layers.`);
    }

    const scene = texture.getScene();
    if (!scene) {
        throw new Error("Cannot upload to a 2D array texture that is not attached to a scene.");
    }

    const engine = scene.getEngine();
    // updateTextureArrayLayerFromImageSource is an opt-in engine extension. When the consumer never
    // imported it, the augmented method is missing, so fail early with the standard side-effect import
    // message (matching the engine.rawTexture family) instead of a generic "is not a function" error.
    if (!_IsSideEffectImplemented(engine.updateTextureArrayLayerFromImageSource)) {
        throw _WarnImport("engine.texture2DArrayImageSource");
    }

    engine.updateTextureArrayLayerFromImageSource(internalTexture, source, layer, options?.invertY ?? false, options?.premultiplyAlpha ?? false);
}

/**
 * Fetches an image from a url, decodes it and uploads it into a single layer of a 2D array texture.
 * @param texture defines the 2D array texture to upload into
 * @param url defines the url of the image to load
 * @param layer defines the array layer to upload into
 * @param options defines optional upload settings (invertY, premultiplyAlpha)
 * @returns a promise resolved once the layer has been uploaded
 */
export async function LoadImageToTexture2DArrayLayerAsync(
    texture: RawTexture2DArray,
    url: string,
    layer: number,
    options?: IUploadImageToTexture2DArrayLayerOptions
): Promise<void> {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch image "${url}": ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    const bitmap = await createImageBitmap(blob);
    try {
        UploadImageToTexture2DArrayLayer(texture, bitmap, layer, options);
    } finally {
        bitmap.close();
    }
}

/**
 * Options controlling the creation of a 2D array texture from a KTX2 file.
 */
export interface ICreateTexture2DArrayFromKTX2Options {
    /** Defines if mip levels should be generated (true by default) */
    generateMipMaps?: boolean;
    /** Defines the sampling mode to use (Texture.TRILINEAR_SAMPLINGMODE by default) */
    samplingMode?: number;
    /** Defines if the texture must be stored with the Y axis inverted (false by default) */
    invertY?: boolean;
}

/**
 * Creates a 2D array texture from a single KTX2 file holding several array layers (layerCount greater than 1).
 * This is the single-file counterpart to CreateTexture2DArrayFromImageUrlsAsync: rather than fetching one image
 * per layer, the whole array travels in one container.
 *
 * The data is transcoded to uncompressed RGBA. Keeping the texture in its transcoded compressed form would
 * require compressedTexImage3D support in the engine raw texture path, which does not exist yet, so the
 * GPU cost here matches a plain RGBA array texture.
 *
 * Only the base mip level stored in the file is uploaded; when generateMipMaps is on, the remaining levels are
 * regenerated by the engine. Uploading the file's own mip chain needs per-mip array uploads, which WebGL's
 * updateRawTexture2DArray does not support today.
 * @param scene defines the hosting scene
 * @param data defines the url of the KTX2 file, or its already fetched content
 * @param options defines optional creation settings
 * @returns a promise resolved with the created RawTexture2DArray
 */
export async function CreateTexture2DArrayFromKTX2Async(scene: Scene, data: string | ArrayBufferView, options?: ICreateTexture2DArrayFromKTX2Options): Promise<RawTexture2DArray> {
    let buffer: ArrayBufferView;
    if (typeof data === "string") {
        const response = await fetch(data);
        if (!response.ok) {
            throw new Error(`Failed to fetch KTX2 file "${data}": ${response.status} ${response.statusText}`);
        }
        buffer = new Uint8Array(await response.arrayBuffer());
    } else {
        buffer = data;
    }

    const { KhronosTextureContainer2 } = await import("../../Misc/khronosTextureContainer2");

    if (!KhronosTextureContainer2.IsValid(buffer)) {
        throw new Error("The provided data is not a valid KTX2 file.");
    }

    const container = new KhronosTextureContainer2(scene.getEngine());

    // forceRGBA: the transcoded compressed formats cannot be uploaded to an array texture yet (see above).
    const decodedData = await container._decodeAsync(buffer, { forceRGBA: true });

    if (decodedData.errors) {
        throw new Error("Failed to decode the KTX2 file. " + decodedData.errors);
    }

    const layerCount = Math.max(decodedData.layerCount ?? 1, 1);
    // mipmaps are ordered level by level, and within a level layer by layer, so the base level occupies
    // the first layerCount entries and its layers are already in ascending order.
    const baseLevel = decodedData.mipmaps.slice(0, layerCount);

    if (baseLevel.length !== layerCount) {
        throw new Error(`Failed to decode the KTX2 file: expected ${layerCount} layers for the base mip level but got ${baseLevel.length}.`);
    }

    // Every layer of a level shares the level's dimensions, and forceRGBA above means each one must be exactly
    // width * height * 4 bytes. RawTexture2DArray takes all the layers as one flat buffer and cannot detect a
    // short or mismatched layer, so validate here rather than uploading malformed data.
    const { width, height } = baseLevel[0];
    const expectedLayerByteLength = width * height * 4;

    for (const mipmap of baseLevel) {
        if (!mipmap.data) {
            throw new Error(`Failed to decode the KTX2 file: layer ${mipmap.layerIndex} of the base mip level is empty.`);
        }
        if (mipmap.width !== width || mipmap.height !== height) {
            throw new Error(
                `Failed to decode the KTX2 file: layer ${mipmap.layerIndex} of the base mip level is ${mipmap.width}x${mipmap.height} but layer 0 is ${width}x${height}.`
            );
        }
        if (mipmap.data.byteLength !== expectedLayerByteLength) {
            throw new Error(
                `Failed to decode the KTX2 file: layer ${mipmap.layerIndex} of the base mip level holds ${mipmap.data.byteLength} bytes but ${expectedLayerByteLength} were expected.`
            );
        }
    }

    // A 2D array texture is uploaded as a single buffer holding every layer back to back.
    const textureData = new Uint8Array(expectedLayerByteLength * layerCount);
    let byteOffset = 0;
    for (const mipmap of baseLevel) {
        textureData.set(mipmap.data!, byteOffset);
        byteOffset += mipmap.data!.byteLength;
    }

    return new RawTexture2DArray(
        textureData,
        width,
        height,
        layerCount,
        Constants.TEXTUREFORMAT_RGBA,
        scene,
        options?.generateMipMaps ?? true,
        options?.invertY ?? false,
        options?.samplingMode,
        Constants.TEXTURETYPE_UNSIGNED_BYTE
    );
}

/**
 * Creates a 2D array texture and fills each layer from a list of image urls.
 * All images must share the same dimensions.
 * @param scene defines the hosting scene
 * @param urls defines the url of the image for each layer (at least one)
 * @param options defines optional creation and upload settings
 * @returns a promise resolved with the created RawTexture2DArray
 */
export async function CreateTexture2DArrayFromImageUrlsAsync(
    scene: Scene,
    urls: readonly [string, ...string[]],
    options?: ICreateTexture2DArrayFromImageUrlsOptions
): Promise<RawTexture2DArray> {
    // allSettled (not all): a rejected fetch/decode must not leak the layers that
    // already decoded. Promise.all rejects on the first failure and never enters the
    // try/finally below, orphaning every ImageBitmap that resolved. Close the
    // fulfilled ones here, then rethrow the original failure.
    const results = await Promise.allSettled(
        urls.map(async (url) => {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch image "${url}": ${response.status} ${response.statusText}`);
            }
            return await createImageBitmap(await response.blob(), options?.imageBitmapOptions);
        })
    );

    const firstRejection = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
    if (firstRejection) {
        for (const result of results) {
            if (result.status === "fulfilled") {
                result.value.close();
            }
        }
        throw firstRejection.reason;
    }

    const bitmaps = results.filter((result): result is PromiseFulfilledResult<ImageBitmap> => result.status === "fulfilled").map((result) => result.value) as [
        ImageBitmap,
        ...ImageBitmap[],
    ];

    try {
        const width = bitmaps[0].width;
        const height = bitmaps[0].height;

        for (let index = 1; index < bitmaps.length; index++) {
            const bitmap = bitmaps[index];
            if (bitmap.width !== width || bitmap.height !== height) {
                throw new Error(`All images must share the same dimensions. Image at index ${index} is ${bitmap.width}x${bitmap.height}, expected ${width}x${height}.`);
            }
        }

        const texture = new RawTexture2DArray(
            null,
            width,
            height,
            bitmaps.length,
            Constants.TEXTUREFORMAT_RGBA,
            scene,
            options?.generateMipMaps ?? true,
            options?.invertY ?? false,
            options?.samplingMode,
            options?.textureType
        );

        try {
            const uploadOptions: IUploadImageToTexture2DArrayLayerOptions = {
                invertY: options?.invertY ?? false,
                premultiplyAlpha: options?.premultiplyAlpha ?? false,
            };

            // updateTextureArrayLayerFromImageSource rebuilds the whole mip chain on each upload when mips
            // are enabled. Uploading N layers that way is O(N) redundant mip generation, so suppress it
            // per-layer and let only the final upload regenerate the mips once — a single generateMipmap
            // covers every layer of the array.
            const internalTexture = texture.getInternalTexture();
            const generateMipMaps = internalTexture?.generateMipMaps ?? false;
            if (internalTexture && generateMipMaps) {
                internalTexture.generateMipMaps = false;
            }

            for (let layer = 0; layer < bitmaps.length; layer++) {
                if (internalTexture && generateMipMaps && layer === bitmaps.length - 1) {
                    internalTexture.generateMipMaps = true;
                }
                UploadImageToTexture2DArrayLayer(texture, bitmaps[layer], layer, uploadOptions);
            }

            return texture;
        } catch (error) {
            // A layer upload can throw (out-of-range layer, missing engine extension, backend upload
            // failure). Dispose the freshly allocated GPU texture so it does not leak, then rethrow.
            texture.dispose();
            throw error;
        }
    } finally {
        for (let index = 0; index < bitmaps.length; index++) {
            bitmaps[index].close();
        }
    }
}
